import { useRef, useState, useEffect } from "react";
import { graphql } from "gatsby";
import {
  extract,
  EventAggregator,
  BrowserSupportHelper,
  PrimaryButtonLink,
  useUiBkgVideoWorkaround,
  SimplifyGatsbyQuery,
  HeroBlock as CoreHeroBlock,
  withLivePreview,
  heroBlockLivePreviewConfig,
  heroBlockContentfulToCoreProps,
} from "@parksandresorts/core";
import UiBkgVideo from "@rnb/ui-bkg-video";
import styles from "./hero-block.module.less";
import Parallax from "components/block-elements/parallax/parallax";
import LinkWrapper from "components/block-elements/link-wrapper/link-wrapper";
import UiPicture from "@rnb/ui-picture";

const breakPointSmallDevice = 576;

const OldHeroBlock = (props: SimplifyGatsbyQuery<Queries.HeroBlockFragment>) => {
  const videoKey = useUiBkgVideoWorkaround(breakPointSmallDevice);
  const imageUrlForSmallDevice = props.heroImageSmallDevice?.file?.url;
  const imageUrlForLargeDevice = props.heroImageLargeDevice?.file?.url;
  const buttonLinkInternalLink = props.buttonLink?.internalLink?.slug;
  const buttonLinkExternalLink = props.buttonLink?.externalUrl;
  const videoUrl = props.backgroundVideo?.file?.url;
  const videoUrlMobile = props.backgroundVideoMobile?.file?.url;
  const textPositioning = props.textPositioning;
  const enableTextBackground = props.enableTextBackground;
  const componentTheme = props.componentTheme;
  const heroTextRef = useRef<HTMLSpanElement>(null);

  const isInternetExplorerOrEdge = BrowserSupportHelper.checkIsBrowserEdge() || BrowserSupportHelper.checkIsBrowserIE();

  const buttonLink = buttonLinkExternalLink ?? buttonLinkInternalLink ?? "";

  const [heroBackgroundWidth, setHeroBackgroundWidth] = useState<number | null>(null);
  const [heroBackgroundHeight, setHeroBackgroundHeight] = useState<number | null>(null);

  useEffect(() => {
    if (enableTextBackground && heroTextRef.current) {
      const heroTextBoundingRect = heroTextRef.current.getBoundingClientRect();
      const height = heroTextBoundingRect.height * 1.3;
      const width = height > 80 ? heroTextBoundingRect.width + 64 : heroTextBoundingRect.width * 1.2;
      setHeroBackgroundHeight(height);
      setHeroBackgroundWidth(width);
    }
  }, [enableTextBackground]);

  const getPicture = () => {
    let smallDevice = props.heroImageSmallDevice ?? "";
    const largeDevice = props.heroImageLargeDevice ?? "";

    if (!smallDevice) {
      smallDevice = largeDevice;
    }

    return {
      id: `${props.contentfulId}_hero`,
      src: `${largeDevice.file.url}?w=1402&q=70`,
      loading: "eager",
      decode: "sync",
      alt: extract(largeDevice, "description", ""),
      width: extract(largeDevice, "file.details.image.width", undefined),
      height: extract(largeDevice, "file.details.image.height", undefined),
      sources: [
        {
          srcset: `${smallDevice.file.url}?w=375&q=70&fm=webp, ${smallDevice.file.url}?w=563&q=70&fm=webp 2x`,
          media: `(max-width: 767px)`,
          type: "image/webp",
        },
        {
          srcset: `${smallDevice.file.url}?w=375&q=70, ${smallDevice.file.url}?w=563&q=70&fm=webp 2x`,
          media: `(max-width: 767px)`,
        },
        {
          srcset: `${largeDevice.file.url}?w=1401&q=70&fm=webp, ${largeDevice.file.url}?w=2880&q=70&fm=webp 2x`,
          media: `(min-width: 1200px)`,
          type: "image/webp",
        },
        {
          srcset: `${largeDevice.file.url}?w=1401&q=70, ${largeDevice.file.url}?w=2880&q=70 2x`,
          media: `(min-width: 1200px)`,
        },
        {
          srcset: `${largeDevice.file.url}?w=768&q=70&fm=webp, ${largeDevice.file.url}?w=1500&q=70&fm=webp 2x`,
          media: `(min-width: 768px)`,
          type: "image/webp",
        },
        {
          srcset: `${largeDevice.file.url}?w=768&q=70&fm=webp, ${largeDevice.file.url}?w=1500&q=70&fm=webp 2x`,
          media: `(min-width: 768px)`,
        },
      ],
    };
  };

  const getLinkWrapperUrl = () => {
    if (buttonLink) {
      return !props.buttonLabel ? buttonLink : null;
    }
    return null;
  };

  const renderHeroTextAndDecoration = () => (
    <LinkWrapper url={getLinkWrapperUrl()}>
      <div className={styles.hero__infoOuterwrapper}>
        <div className={[styles.hero__infoInnerwrapper, styles[textPositioning]].join(" ")}>
          {props.entryTitle && (
            <div className={styles.hero__entryTitleWrapper}>
              <h1
                className={[
                  styles.hero__header,
                  enableTextBackground ? styles.hero__colorText : styles.hero__textShadow,
                  "hero-heading",
                ].join(" ")}
              >
                <span ref={heroTextRef}>{props.entryTitle}</span>
              </h1>
              <div
                style={{
                  width: heroBackgroundWidth + "px",
                  height: heroBackgroundHeight + "px",
                }}
                className={styles.background}
              />
            </div>
          )}
          {props.heroParagraph && (
            <p className={[styles.hero__paragraph, enableTextBackground ? "" : styles.hero__textShadow].join(" ")}>
              <span className={enableTextBackground ? styles.hero__paragraphBackground : ""}>
                {props.heroParagraph}
              </span>
            </p>
          )}
          {props.buttonLabel && buttonLink && (
            <div className={styles.buttonWrapper}>
              <span
                onClick={() => {
                  EventAggregator.publish("trackEvent", {
                    category: "CTA Click",
                    action: props.buttonLabel,
                    label: buttonLink,
                  });
                }}
              >
                <PrimaryButtonLink label={props.buttonLabel} className={styles.ctaButton} url={buttonLink} />
              </span>
            </div>
          )}
        </div>
      </div>
    </LinkWrapper>
  );

  return (
    <div
      className={[
        "top-block block--full-width block--no-margin",
        componentTheme ? `component-theme-${componentTheme}` : "",
      ].join(" ")}
      data-contentful-id={props.contentfulId}
    >
      {videoUrl ? (
        <Parallax parallaxOffset={64}>
          <UiBkgVideo
            key={videoKey}
            wrapperExtraClasses={styles.hero__videoWrapper}
            videoExtraClasses={[
              styles.hero__video,
              "data-block-in-nav-parallax",
              isInternetExplorerOrEdge ? styles.hero__video_ie : styles.hero__video_normal,
            ].join(" ")}
            srcDesktop={videoUrl}
            srcMobile={videoUrlMobile ? videoUrlMobile : undefined}
            autoplay="true"
            muted={true}
            preload="auto"
            playsinline={true}
            loop={true}
            breakPointSmallDevice={breakPointSmallDevice}
            posterSmallDevice={imageUrlForSmallDevice ? imageUrlForSmallDevice + "?q=30" : undefined}
            posterLargeDevice={imageUrlForLargeDevice ? imageUrlForLargeDevice + "?q=30" : undefined}
            videoPosterExtraClass={styles.hero__videoPoster}
            videoPosterHiddenClass={styles.hero__videoPosterHidden}
          >
            {renderHeroTextAndDecoration()}
            <div className={styles.hero__gradientLayer} />
          </UiBkgVideo>
        </Parallax>
      ) : (
        <Parallax parallaxOffset={64}>
          <UiPicture {...getPicture()} useLowQualityImage={true} classNamePictureWrapper={styles.heroPictureWrapper} />
          <div className={styles.hero__gradientLayer} />
          {renderHeroTextAndDecoration()}
        </Parallax>
      )}
    </div>
  );
};

const HeroBlock = (props: SimplifyGatsbyQuery<Queries.HeroBlockFragment>) => {
  if (!props.useNewHeroBlock) {
    return <OldHeroBlock {...props} />;
  }

  return (
    <CoreHeroBlock
      nextBlockTypesBlockingPausePlay={["ContentfulLink", "ContentfulRestaurantBlock"]}
      {...heroBlockContentfulToCoreProps(props)}
    />
  );
};

export default withLivePreview(HeroBlock, heroBlockLivePreviewConfig);

export const fragmentHeroBlock = graphql`
  fragment HeroBlock on ContentfulHeroBlock {
    ... on ContentfulHeroBlock {
      contentfulId: contentful_id
      id
      entryTitle
      heroParagraph
      paragraphHasBackground
      buttonLabel
      textPositioning
      enableTextBackground
      componentTheme
      useNewHeroBlock
      useFirstTitle
      firstTitle {
        raw
      }
      firstTitleSize
      firstTitleTiltDegrees
      firstTitleHasDropShadow
      firstTitleBackground
      useSecondTitle
      secondTitle {
        raw
      }
      secondTitleSize
      secondTitleTiltDegrees
      secondTitleHasDropShadow
      secondTitleBackground
      textAlignment
      contentPosition
      size
      buttonLink {
        ...Link
      }
      heroImageSmallDevice {
        ...Asset
        gatsbyImageData(aspectRatio: 1, quality: 70, resizingBehavior: FILL)
      }
      logoImage {
        description
        mobile: gatsbyImageData(width: 50, resizingBehavior: FILL)
        desktop: gatsbyImageData(width: 100, resizingBehavior: FILL)
      }
      logoPosition
      heroImageLargeDevice {
        ...Asset
        gatsbyImageData(aspectRatio: 3, quality: 70, resizingBehavior: FILL)
        ultraWideBlur: gatsbyImageData(aspectRatio: 4, placeholder: BLURRED)
      }
      backgroundVideo {
        file {
          url
        }
      }
      backgroundVideoMobile {
        file {
          url
        }
      }
      repeatingPattern {
        highQualityMobile: gatsbyImageData(width: 150, quality: 80, resizingBehavior: FILL)
        lowQualityMobile: gatsbyImageData(width: 50, quality: 50, resizingBehavior: FILL)
        highQualityDesktop: gatsbyImageData(width: 300, quality: 80, resizingBehavior: FILL)
        lowQualityDesktop: gatsbyImageData(width: 100, quality: 50, resizingBehavior: FILL)
        file {
          url
        }
      }
      repeatingPatternOpacity
      customColors {
        backgroundColor
        iconBackgroundColor
        iconTextColor
        firstTitleBackgroundColor
        firstTitleTextColor
        firstTitleDropShadowColor
        secondTitleBackgroundColor
        secondTitleTextColor
        secondTitleDropShadowColor
        buttonBackgroundColor
        buttonTextColor
      }
    }
  }
`;
